import type { FC } from 'react'

import { SelectableCard } from '@skytech/ui'
import CheckboxBlankOutlineIcon from 'mdi-react/CheckboxBlankOutlineIcon'
import CheckboxMarkedOutlineIcon from 'mdi-react/CheckboxMarkedOutlineIcon'
import { twJoin } from 'tailwind-merge'

import type { Classification } from '@/modules/ecom-policy/new/types'
import handleMultipleChoiceItemSelect from '@/util/handleMultipleChoiceItemSelect'

const ClassificationTile: FC<{
  symbol: Classification
  label: string
  selectedClassifications: Classification[] | null
  setSelectedClassifications: (v: Classification[] | null) => void
  size?: 'lg' | 'md' | 'sm'
  isDisabled?: boolean
  labelClassName?: string
}> = ({
  symbol,
  label,
  selectedClassifications,
  setSelectedClassifications,
  size = 'lg',
  isDisabled = false,
  labelClassName,
}) => {
  const isSelected = selectedClassifications?.includes(symbol)

  const handleClassificationSelected = () => {
    if (isDisabled) return
    if (symbol === 'ALL' && selectedClassifications?.includes(symbol)) {
      setSelectedClassifications(null)

      return
    }

    if (symbol === 'ALL') {
      setSelectedClassifications([symbol])

      return
    }

    let updatedClassificationSymbols = [...(selectedClassifications || [])]

    if (selectedClassifications?.includes('ALL')) {
      updatedClassificationSymbols = selectedClassifications?.filter(symbol => symbol !== 'ALL')
    }

    handleMultipleChoiceItemSelect<Classification>({
      selectedId: symbol,
      currentIdList: updatedClassificationSymbols,
      setData: setSelectedClassifications,
    })
  }

  return (
    <div>
      <SelectableCard
        bordered
        padding={4}
        className={twJoin(
          'max-h-20',
          size === 'lg' && 'min-h-[106px]',
          size === 'md' && 'min-h-[60px]',
          // todo: remove important SelectableCard styling is refactored
          size === 'sm' && 'min-h-[40px] !border'
        )}
        colorMode={symbol === 'ALL' ? 'blue' : 'yellow'}
        isSelected={isSelected}
        disabled={isDisabled}
        onClick={handleClassificationSelected}
        flex
      >
        <h3
          className={twJoin(
            'm-0 w-full self-center leading-5',
            isSelected && '!font-bold',
            size === 'lg' && '-mr-8 pl-4 text-center text-xl font-bold',
            size === 'md' && 'text-md -mr-7 pl-2 text-center font-bold',
            size === 'sm' && 'pl-1 text-left text-sm font-normal',
            labelClassName
          )}
        >
          {label}
        </h3>

        <div
          className={twJoin(
            'checkmark m-1 w-fit',
            size === 'lg' && 'self-start',
            size === 'md' && 'self-start',
            size === 'sm' && 'self-center'
          )}
        >
          {isSelected ? (
            <CheckboxMarkedOutlineIcon
              className={twJoin(
                size === 'lg' && 'h-8 w-8',
                size === 'md' && 'h-7 w-7',
                size === 'sm' && 'h-6 w-6'
              )}
            />
          ) : (
            <CheckboxBlankOutlineIcon
              className={twJoin(
                size === 'lg' && 'h-8 w-8',
                size === 'md' && 'h-7 w-7',
                size === 'sm' && 'h-6 w-6'
              )}
            />
          )}
        </div>
      </SelectableCard>
    </div>
  )
}

export default ClassificationTile
